/**
 * @fileoverview Key Date Side Panel
 *
 * Purpose:
 *  - Single side-panel surface that handles three modes — view, edit,
 *    and create — for a Morgan KeyDate. Opens from the calendar's
 *    `onEventClick` (view/edit) and `onDateCellClick` / header
 *    "+ Add key date" button (create).
 *  - Replaces the bundled big-calendar event dialogs so we own the
 *    UX (Morgan DatePicker, status pills, derived-event treatment).
 *
 * Dependencies:
 *  - Sheet from @/ui/composed/overlays
 *  - DatePicker from @/ui/composed/inputs/date-picker
 *  - Convex mutations: keyDates.{createManual, update, updateNotes,
 *    rescheduleDerived, remove}; tasks.{updateTask, setTaskStatus,
 *    completeAllSubtasks}
 *
 * Used by:
 *  - src/routes/_app/_auth/dashboard/_layout.jobs.$jobId.calendar.tsx
 *  - src/routes/_app/_auth/dashboard/_layout.calendar.tsx
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useMutation, useQuery } from "convex/react";
import { format, parseISO } from "date-fns";
import {
  CalendarDays,
  CheckCircle2,
  Pencil,
  Trash2,
  X,
  XCircle,
} from "lucide-react";

import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import type {
  KeyDateCategory,
  KeyDateFlag,
} from "@cvx/keyDates/jobKeyDates/schema";

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/ui/composed/overlays/sheet";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/ui/composed/sections/tabs";
import { ScrollArea } from "@/ui/primitives/scroll-area";
import { cn } from "@/utils/misc";
import { Button } from "@/ui/primitives/button";
import { Input } from "@/ui/primitives/input";
import { Textarea } from "@/ui/primitives/textarea";
import { Label } from "@/ui/primitives/label";
import { Badge } from "@/ui/primitives/badge";
import { Switch } from "@/ui/primitives/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import { DatePicker } from "@/ui/composed/inputs/date-picker";
import { SearchableSelect } from "@/ui/composed/inputs/searchable-select";
import { TaskSubscriptionBell } from "@/inbox/task-subscription-bell";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/ui/composed/overlays/tooltip";
import {
  ChangeHistory,
  type ChangeHistoryEntry,
  type HistoryFieldFormatters,
  type HistoryFieldLabels,
  type ResolvedHistoryUser,
} from "@/components/jobs/phases/gather/summarization/shared-panels/ChangeHistory";
import { getDisplayName, UserAvatar } from "@/ui/composed";

import { isEventKind, type CalendarEventRow } from "@/calendar/eventAdapter";
import {
  CATEGORY_META,
  TASK_CATEGORY_ORDER,
  isKeyDateCategory,
  typeBadgeMeta,
} from "@/calendar/categoryMeta";
import { priorityMeta, TASK_PRIORITIES } from "@/calendar/task-priority";
import { useActiveOrg } from "@/utils/org-context";
import { TaskDetailsSection } from "@/calendar/task-details-section";
import { TaskAttachmentsSection } from "@/calendar/task-attachments-section";
import { TaskTimingSection } from "@/calendar/task-timing-section";
import { TaskDependenciesSection } from "@/calendar/task-dependencies-section";
import { TaskAutomationsSection } from "@/calendar/task-automations-section";
import { TaskRecurrenceSection } from "@/calendar/task-recurrence-section";
import { TaskNotesFeed } from "@/calendar/task-notes-feed";
import {
  TaskPanelNavProvider,
  type TaskPanelNav,
} from "@/calendar/task-panel-nav";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type KeyDatePanelMode =
  | { kind: "closed" }
  | { kind: "view"; event: CalendarEventRow }
  | { kind: "edit"; event: CalendarEventRow }
  | {
      kind: "create";
      jobId: Id<"jobs">;
      initialDate: string;
      initialTime?: string;
      /** End time for a dragged range (HH:mm); seeds a timed event. */
      initialEndTime?: string;
      /** Default Type on open (task boards default to `task`; calendar `event`). */
      initialKind?: "event" | "task";
      /** Pre-select an assignee (e.g. the "My Tasks" inbox defaults to you). */
      initialAssigneeUserId?: string;
    };

interface KeyDateSidePanelProps {
  state: KeyDatePanelMode;
  onClose: () => void;
  /**
   * Optional list of jobs the practitioner can pick from when creating
   * a new key date from the workspace calendar (where there is no
   * fixed `jobId` on the page).
   */
  jobOptions?: Array<{ id: Id<"jobs">; name: string }>;
}

const KEY_DATE_FIELD_LABELS: HistoryFieldLabels = {
  title: "Title",
  description: "Description",
  date: "Date",
  endDate: "End date",
  notes: "Notes",
  status: "Status",
  statusId: "Status",
  category: "Category",
  flag: "Flag",
  allDay: "All day",
  startTime: "Start time",
  endTime: "End time",
  assigneeUserId: "Assignee",
  assignorUserId: "Assigned by",
  priority: "Priority",
  labelIds: "Labels",
  parentTaskId: "Parent task",
  plannedDate: "Planned from",
  plannedEndDate: "Planned to",
  plannedAllDay: "Planned all day",
  plannedStartTime: "Planned start",
  plannedEndTime: "Planned end",
  kind: "Type",
};

/** Every selectable category, labelled from the shared registry (MOR-707). */
const CATEGORY_OPTIONS: Array<{ value: KeyDateCategory; label: string }> =
  TASK_CATEGORY_ORDER.map((value) => ({
    value,
    label: CATEGORY_META[value].label,
  }));

/** Type axis offered on create / in the detail panel (MOR-707 follow-up). */
const TYPE_OPTIONS: Array<{ value: "event" | "task"; label: string }> = [
  { value: "event", label: "Event" },
  { value: "task", label: "Task" },
];

const FLAG_OPTIONS: Array<{
  value: KeyDateFlag;
  label: string;
  description: string;
}> = [
  {
    value: "must",
    label: "Required",
    description: "Required in every appointment",
  },
  {
    value: "event_driven",
    label: "Conditional on other event",
    description: "Conditional on another event",
  },
  {
    value: "recurring",
    label: "Recurring (~)",
    description: "Repeats over the life of the matter",
  },
];

// ---------------------------------------------------------------------------
// Public component
// ---------------------------------------------------------------------------

/**
 * Fixed width of a single panel column. Wider than the original 28rem so the
 * tabbed detail surface (Details / Notes / History) has room to breathe.
 */
const COLUMN_WIDTH_REM = 34;

/**
 * A pushed side-panel column. Either an existing row opened for viewing, or a
 * blank "create task" form pre-parented to another row (used to author a
 * subtask with full details).
 */
type StackColumn =
  | { type: "view"; row: CalendarEventRow }
  | {
      type: "create";
      key: string;
      parentId: Id<"jobKeyDates">;
      jobId: Id<"jobs">;
      date: string;
    };

export function KeyDateSidePanel({
  state,
  onClose,
  jobOptions,
}: KeyDateSidePanelProps) {
  const open = state.kind !== "closed";

  // Stack of related tasks opened beside the base panel (deps / subtasks /
  // parent). The base event is the first column; pushing appends columns to
  // the right so the practitioner can compare tasks side by side.
  const [stack, setStack] = useState<StackColumn[]>([]);

  const baseEvent =
    state.kind === "view" || state.kind === "edit" ? state.event : null;
  const baseKey = baseEvent ? baseEvent.id : state.kind;

  // Reset the stack whenever the base panel target changes (or it closes).
  useEffect(() => {
    setStack([]);
  }, [baseKey]);

  const openRelated = useCallback(
    (row: CalendarEventRow) => {
      setStack((prev) => {
        const alreadyOpen =
          (baseEvent && baseEvent.id === row.id) ||
          prev.some((p) => p.type === "view" && p.row.id === row.id);
        return alreadyOpen ? prev : [...prev, { type: "view", row }];
      });
    },
    [baseEvent],
  );

  const openCreateChild = useCallback((parent: CalendarEventRow) => {
    const parentId = parent.persistedId;
    if (!parentId) return;
    setStack((prev) => [
      ...prev,
      {
        type: "create",
        key: `create-${parent.id}-${prev.length}`,
        parentId,
        jobId: parent.jobId,
        date: parent.date,
      },
    ]);
  }, []);

  const nav = useMemo<TaskPanelNav>(
    () => ({ openRelated, openCreateChild }),
    [openRelated, openCreateChild],
  );

  const isStackable = state.kind === "view" || state.kind === "edit";
  const columnCount = isStackable ? 1 + stack.length : 1;

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent
        side="right"
        hideDefaultClose
        className="flex w-full flex-row gap-0 overflow-x-auto p-0"
        style={{
          width: `${columnCount * COLUMN_WIDTH_REM}rem`,
          maxWidth: "100vw",
        }}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <TaskPanelNavProvider value={nav}>
          {isStackable && baseEvent && (
            <>
              <PanelColumn>
                <ViewPanel event={baseEvent} onClose={onClose} />
              </PanelColumn>
              {stack.map((item, index) => (
                <PanelColumn
                  key={item.type === "view" ? item.row.id : item.key}
                  layered
                >
                  {item.type === "view" ? (
                    <ViewPanel
                      event={item.row}
                      stacked
                      onClose={() => setStack((prev) => prev.slice(0, index))}
                    />
                  ) : (
                    <CreatePanel
                      jobId={item.jobId}
                      initialDate={item.date}
                      initialKind="task"
                      initialParentId={item.parentId}
                      onClose={() => setStack((prev) => prev.slice(0, index))}
                    />
                  )}
                </PanelColumn>
              ))}
            </>
          )}
          {state.kind === "create" && (
            <PanelColumn>
              <CreatePanel
                jobId={state.jobId}
                initialDate={state.initialDate}
                initialTime={state.initialTime}
                initialEndTime={state.initialEndTime}
                initialKind={state.initialKind}
                initialAssigneeUserId={state.initialAssigneeUserId}
                jobOptions={jobOptions}
                onClose={onClose}
              />
            </PanelColumn>
          )}
        </TaskPanelNavProvider>
      </SheetContent>
    </Sheet>
  );
}

/** One self-contained, vertically-scrolling column inside the side panel. */
function PanelColumn({
  children,
  layered = false,
}: {
  children: ReactNode;
  /**
   * Stacked columns (opened beside the base) float above the column to their
   * left with a leftward shadow, mirroring the linked-record detail panels
   * (security interest ↔ asset) so the layers read as distinct cards.
   */
  layered?: boolean;
}) {
  return (
    <div
      className={cn(
        "flex h-full shrink-0 flex-col border-l bg-background first:border-l-0",
        layered && "shadow-[-16px_0_32px_-12px_rgba(0,0,0,0.18)]",
      )}
      style={{ width: `${COLUMN_WIDTH_REM}rem`, maxWidth: "100vw" }}
    >
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// View panel
// ---------------------------------------------------------------------------

function ViewPanel({
  event: propEvent,
  onClose,
  stacked = false,
}: {
  event: CalendarEventRow;
  onClose: () => void;
  /** True when this column was pushed beside another (deps / subtasks). */
  stacked?: boolean;
}) {
  // The panel is opened with a snapshot of the row captured at click time.
  // Re-resolve it from the live job query so edits (status, priority,
  // assignee, …) reflect immediately without closing and reopening. Match by
  // `persistedId` when present, else by `code` (so a derived event that
  // materialises into an override row on first touch keeps resolving), else by
  // the stable wire `id`.
  const liveEvents = useQuery(api.keyDates.queries.listForJob, {
    jobId: propEvent.jobId,
  });
  const event = useMemo<CalendarEventRow>(() => {
    const list = liveEvents ?? [];
    const match = propEvent.persistedId
      ? list.find((e) => e.persistedId === propEvent.persistedId)
      : propEvent.code
        ? list.find((e) => e.code === propEvent.code)
        : list.find((e) => e.id === propEvent.id);
    return match ?? propEvent;
  }, [liveEvents, propEvent]);

  const setTaskStatus = useMutation(api.tasks.mutations.setTaskStatus);
  const updateKeyDate = useMutation(api.keyDates.mutations.update);
  const updateNotes = useMutation(api.keyDates.mutations.updateNotes);
  const rescheduleDerived = useMutation(
    api.keyDates.mutations.rescheduleDerived,
  );
  const remove = useMutation(api.keyDates.mutations.remove);
  const dismissDerived = useMutation(api.keyDates.mutations.dismissDerived);

  // Identity of the calling user — decides which note rows are editable in
  // the Notes tab's chronological feed.
  const currentUser = useQuery(api.users.queries.getCurrentUser, {});
  const currentUserId = currentUser?._id;

  // Firm statuses drive the complete / reopen shortcuts: "complete" jumps to
  // the first `completed`-type status, "reopen" to the first `unstarted` one.
  const statuses = useQuery(api.tasks.statuses.listByOrg, {
    orgId: event.orgId,
  });
  // Resolve ids stored in change-history diffs (status / label / parent /
  // priority / assignee) back into human labels for the history feed.
  const labels = useQuery(api.tasks.labels.listByOrg, { orgId: event.orgId });
  const jobEvents = useQuery(api.keyDates.queries.listForJob, {
    jobId: event.jobId,
  });
  const completedStatusId = useMemo(
    () => statuses?.find((s) => s.type === "completed")?._id ?? null,
    [statuses],
  );
  const reopenStatusId = useMemo(
    () => statuses?.find((s) => s.type === "unstarted")?._id ?? null,
    [statuses],
  );
  const isTerminal =
    event.statusType === "completed" || event.statusType === "canceled";

  const [editingField, setEditingField] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState("");
  // Inline edits autosave on blur (click-away). This flag lets a cancel
  // (Escape) skip the blur-save it would otherwise trigger when the field
  // unmounts, so an Escape reverts rather than persisting the draft.
  const skipBlurSaveRef = useRef(false);

  const startEdit = (field: string, currentValue: string) => {
    skipBlurSaveRef.current = false;
    setEditingField(field);
    setEditDraft(currentValue);
  };

  const cancelEdit = () => {
    skipBlurSaveRef.current = true;
    setEditingField(null);
    setEditDraft("");
  };

  const saveEdit = async (field: string, rawValue?: string) => {
    const value = rawValue ?? editDraft;
    // Never persist an empty title — revert to the existing value instead.
    if (field === "title" && value.trim() === "") {
      cancelEdit();
      return;
    }
    if (event.persistedId) {
      await updateKeyDate({ id: event.persistedId, [field]: value });
    } else if (event.code) {
      if (field === "date") {
        await rescheduleDerived({
          jobId: event.jobId,
          code: event.code,
          date: value,
        });
      } else {
        await updateNotes({
          jobId: event.jobId,
          code: event.code,
          notes: value,
        });
      }
    }
    setEditingField(null);
    setEditDraft("");
  };

  // Commit on blur unless a cancel just fired (Escape).
  const handleEditBlur = (field: string) => {
    if (skipBlurSaveRef.current) {
      skipBlurSaveRef.current = false;
      return;
    }
    void saveEdit(field);
  };

  const applyStatus = async (statusId: Id<"taskStatuses">) => {
    if (event.persistedId) {
      await setTaskStatus({ id: event.persistedId, statusId });
    } else if (event.code) {
      await setTaskStatus({ jobId: event.jobId, code: event.code, statusId });
    }
  };

  const handleComplete = async () => {
    if (!completedStatusId) return;
    await applyStatus(completedStatusId);
  };

  const handleReopen = async () => {
    if (!reopenStatusId) return;
    await applyStatus(reopenStatusId);
  };

  const handleDelete = async () => {
    // Derived (auto-generated) events have no row to delete — dismissing
    // hides them permanently instead (MOR-839). Persisted statutory
    // overrides are dismissed server-side by `remove` for the same reason.
    if (event.isDerived && event.code) {
      const confirmed = window.confirm(
        "Remove this auto-generated task from the calendar? It won't come back unless it is restored.",
      );
      if (!confirmed) return;
      await dismissDerived({ jobId: event.jobId, code: event.code });
      onClose();
      return;
    }
    if (!event.persistedId) return;
    await remove({ id: event.persistedId });
    onClose();
  };

  const changeHistory: ChangeHistoryEntry[] = useMemo(
    () => event.changeHistory ?? [],
    [event.changeHistory],
  );

  // Collect every user id referenced by the history — both the entry author
  // (`userId`) and any assignee/assignor ids captured in the before/after
  // diffs — so the feed can render names + avatars instead of raw ids.
  const historyUserIds = useMemo(() => {
    const ids = new Set<string>();
    for (const ch of changeHistory) {
      if (ch.userId) ids.add(ch.userId);
      for (const bag of [ch.previousValues, ch.newValues]) {
        if (!bag) continue;
        for (const key of ["assigneeUserId", "assignorUserId"]) {
          const val = bag[key];
          if (typeof val === "string") ids.add(val);
        }
      }
    }
    return Array.from(ids) as Id<"users">[];
  }, [changeHistory]);

  const resolvedUsers = useQuery(
    api.users.queries.listUsersByIds,
    historyUserIds.length > 0 ? { ids: historyUserIds } : "skip",
  );

  const usersById = useMemo(() => {
    const map = new Map<Id<"users">, ResolvedHistoryUser>();
    for (const u of resolvedUsers ?? []) map.set(u._id, u);
    return map;
  }, [resolvedUsers]);

  // Per-field formatters turn the ids stored in diffs into readable labels.
  const historyFormatters = useMemo<HistoryFieldFormatters>(() => {
    const userName = (value: unknown): string => {
      if (typeof value !== "string") return "\u2014";
      const u = usersById.get(value as Id<"users">);
      return u
        ? getDisplayName(u.firstName, u.lastName, u.email)
        : "Unknown user";
    };
    const statusName = (value: unknown): string => {
      if (typeof value !== "string") return "\u2014";
      return statuses?.find((s) => s._id === value)?.name ?? "\u2014";
    };
    const labelNames = (value: unknown): string => {
      if (!Array.isArray(value) || value.length === 0) return "None";
      return value
        .map((id) => (labels ?? []).find((l) => l._id === id)?.name ?? "label")
        .join(", ");
    };
    const taskTitle = (value: unknown): string => {
      if (typeof value !== "string") return "\u2014";
      return (
        (jobEvents ?? []).find((e) => e.persistedId === value)?.title ?? "task"
      );
    };
    const priorityName = (value: unknown): string =>
      typeof value === "number" ? priorityMeta(value).label : "\u2014";

    return {
      assigneeUserId: userName,
      assignorUserId: userName,
      statusId: statusName,
      labelIds: labelNames,
      parentTaskId: taskTitle,
      priority: priorityName,
    };
  }, [usersById, statuses, labels, jobEvents]);

  return (
    <>
      <SheetHeader className="relative border-b p-6">
        <div className="absolute right-4 top-4 flex items-center gap-1">
          {/* Per-task notification bell (MOR-716) — persisted rows only. */}
          {!event.isDerived && (
            <TaskSubscriptionBell taskId={event.id as Id<"jobKeyDates">} />
          )}
          <button
            type="button"
            onClick={onClose}
            aria-label={stacked ? "Close this task" : "Close"}
            className="rounded-sm p-0.5 text-muted-foreground opacity-70 transition-opacity hover:bg-muted hover:opacity-100"
          >
            <X className="size-4" />
          </button>
        </div>
        <div className="min-w-0 pr-6">
          {editingField === "title" ? (
            <Input
              value={editDraft}
              onChange={(e) => setEditDraft(e.target.value)}
              className="h-8 text-lg font-semibold"
              onBlur={() => handleEditBlur("title")}
              onKeyDown={(e) => {
                // Enter blurs → the blur handler saves (single commit path);
                // Escape reverts.
                if (e.key === "Enter") {
                  e.preventDefault();
                  e.currentTarget.blur();
                }
                if (e.key === "Escape") cancelEdit();
              }}
              autoFocus
            />
          ) : (
            <SheetTitle
              className="group cursor-pointer"
              onClick={() => startEdit("title", event.title)}
            >
              <span>{event.title}</span>
              <Pencil className="ml-2 inline-block size-3 opacity-0 transition-opacity group-hover:opacity-50" />
            </SheetTitle>
          )}
        </div>
        <SheetDescription className="flex items-center gap-2">
          <span>{event.jobName}</span>
        </SheetDescription>
        <div className="flex flex-wrap items-center gap-2 pt-2">
          {/* Type chip — category for events (Meetings, Lodgements, …),
              Task / Subtask for task-kind rows (MOR-707). */}
          {(() => {
            const meta = typeBadgeMeta(
              isEventKind(event),
              event.category,
              Boolean(event.parentTaskId),
            );
            const Icon = meta.icon;
            return (
              <Badge
                variant="outline"
                className={`gap-1 ${meta.iconBgClass} ${meta.iconFgClass} border-transparent`}
              >
                <Icon className="size-3" />
                {meta.label}
              </Badge>
            );
          })()}
          {event.overdue && event.category !== "appointment" && (
            <Badge variant="destructive" className="text-xs">
              Overdue
            </Badge>
          )}
          {event.statusName && (
            <Badge color={event.statusColor ?? "gray"} variant="soft">
              {event.statusName}
            </Badge>
          )}
          {/* Flag (Required / Conditional / Recurring) only describes statutory
              obligations — hide it on user-created rows, which have no `code`
              and always carry the placeholder `event_driven` flag. */}
          {(event.isDerived || Boolean(event.code)) && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge variant="secondary" className="cursor-default">
                    {flagLabel(event.flag)}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-xs text-xs">
                  {event.flag === "event_driven" && event.description
                    ? event.description
                    : (FLAG_OPTIONS.find((o) => o.value === event.flag)
                        ?.description ?? "")}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {event.isDerived && (
            <Badge
              variant="outline"
              className="border-dashed text-muted-foreground"
            >
              Auto-calculated
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2 pt-2">
          <CalendarDays className="size-4 text-muted-foreground" />
          {editingField === "date" ? (
            <DatePicker
              value={editDraft}
              onChange={(v) => {
                // Picking a date commits immediately (autosave).
                setEditDraft(v);
                void saveEdit("date", v);
              }}
            />
          ) : (
            <span
              className="group cursor-pointer text-sm font-medium text-foreground"
              onClick={() => startEdit("date", event.date)}
            >
              {formatDateLabel(event.date)}
              {event.endDate && event.endDate !== event.date
                ? ` – ${formatDateLabel(event.endDate)}`
                : ""}
              <Pencil className="ml-2 inline-block size-3 opacity-0 transition-opacity group-hover:opacity-50" />
            </span>
          )}
        </div>
      </SheetHeader>

      <Tabs defaultValue="details" className="flex min-h-0 flex-1 flex-col">
        <div className="px-6 pt-3">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="details">Details</TabsTrigger>
            <TabsTrigger value="timing">Timing</TabsTrigger>
            <TabsTrigger value="notes">Notes</TabsTrigger>
            <TabsTrigger value="history">History</TabsTrigger>
          </TabsList>
        </div>
        <ScrollArea className="min-h-0 flex-1">
          <TabsContent value="details" className="mt-0 px-6 py-5">
            <div className="space-y-6 text-sm">
              {/* Description first — quickest thing to scan / edit. */}
              <div>
                <h4 className="font-sans text-xs font-semibold uppercase tracking-wider text-foreground">
                  Description
                </h4>
                {editingField === "description" ? (
                  <div className="mt-1.5">
                    <Textarea
                      value={editDraft}
                      onChange={(e) => setEditDraft(e.target.value)}
                      rows={4}
                      className="text-sm"
                      onBlur={() => handleEditBlur("description")}
                      onKeyDown={(e) => {
                        if (e.key === "Escape") cancelEdit();
                      }}
                      autoFocus
                    />
                  </div>
                ) : (
                  <p
                    className="group mt-1.5 cursor-pointer text-sm leading-relaxed text-foreground transition-colors hover:text-primary/80"
                    onClick={() =>
                      startEdit("description", event.description ?? "")
                    }
                  >
                    {event.description || (
                      <span className="italic text-muted-foreground">
                        Click to add description...
                      </span>
                    )}
                    <Pencil className="ml-2 inline-block size-3 opacity-0 transition-opacity group-hover:opacity-50" />
                  </p>
                )}
              </div>

              <TaskDetailsSection event={event} />

              {/* Dependencies — task-only (per spec). */}
              {!isEventKind(event) && <TaskDependenciesSection event={event} />}

              {/* Files — linked job-library documents. Persisted tasks only. */}
              {event.persistedId && (
                <TaskAttachmentsSection
                  keyDateId={event.persistedId}
                  jobId={event.jobId}
                />
              )}

              {/* Automations targeting this task, with a link back to edit each
                  (MOR-736). Renders nothing when none match. */}
              <TaskAutomationsSection event={event} onNavigate={onClose} />
            </div>
          </TabsContent>

          <TabsContent value="timing" className="mt-0 px-6 py-5">
            <div className="space-y-6">
              <TaskTimingSection event={event} />
              {/* Recurrence — available for events and tasks, so ongoing work
                  without a hard deadline can repeat on a cadence. */}
              <div className="border-t pt-5">
                <TaskRecurrenceSection event={event} />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="notes" className="mt-0 px-6 py-5">
            {event.notes && event.notes.trim().length > 0 && (
              <div className="mb-4 rounded-md border border-dashed bg-muted/30 p-3">
                <p className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Earlier note
                </p>
                <p className="whitespace-pre-wrap text-sm text-foreground">
                  {event.notes}
                </p>
              </div>
            )}
            {event.persistedId ? (
              <TaskNotesFeed
                keyDateId={event.persistedId}
                currentUserId={currentUserId}
              />
            ) : (
              <p className="rounded-lg border border-dashed bg-muted/10 p-6 text-center text-sm text-muted-foreground">
                Notes become available once this task is saved.
              </p>
            )}
          </TabsContent>

          <TabsContent value="history" className="mt-0 px-6 py-5">
            {changeHistory.length > 0 ? (
              <ChangeHistory
                history={changeHistory}
                defaultOpen
                fieldLabels={KEY_DATE_FIELD_LABELS}
                formatters={historyFormatters}
                usersById={usersById}
              />
            ) : (
              <p className="rounded-lg border border-dashed bg-muted/10 p-6 text-center text-sm text-muted-foreground">
                No changes recorded yet.
              </p>
            )}
          </TabsContent>
        </ScrollArea>
      </Tabs>

      <SheetFooter className="flex flex-row flex-wrap items-center justify-between gap-2 border-t p-6">
        <div className="flex gap-2">
          {isTerminal ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleReopen}
              disabled={!reopenStatusId}
            >
              <XCircle className="size-4" />
              Reopen
            </Button>
          ) : (
            <Button
              type="button"
              size="sm"
              onClick={handleComplete}
              disabled={!completedStatusId}
            >
              <CheckCircle2 className="size-4" />
              Mark complete
            </Button>
          )}
        </div>

        {(event.persistedId || (event.isDerived && event.code)) && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-destructive hover:text-destructive"
            onClick={handleDelete}
          >
            <Trash2 className="size-4" />
            Delete
          </Button>
        )}
      </SheetFooter>
    </>
  );
}

// ---------------------------------------------------------------------------
// Create panel
// ---------------------------------------------------------------------------

/**
 * Add one hour to a HH:MM string, clamping at 23:59.
 */
function addOneHour(time: string): string {
  const [h, m] = time.split(":").map(Number);
  const newH = Math.min(h + 1, 23);
  return `${String(newH).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/** Sentinel for "no selection" in the create panel's optional pickers. */
const CREATE_NONE = "__none__";

function CreatePanel({
  jobId,
  initialDate,
  initialTime,
  initialEndTime,
  initialKind,
  initialParentId,
  initialAssigneeUserId,
  jobOptions,
  onClose,
}: {
  jobId: Id<"jobs">;
  initialDate: string;
  initialTime?: string;
  initialEndTime?: string;
  initialKind?: "event" | "task";
  initialParentId?: Id<"jobKeyDates">;
  initialAssigneeUserId?: string;
  jobOptions?: Array<{ id: Id<"jobs">; name: string }>;
  onClose: () => void;
}) {
  const createManual = useMutation(api.keyDates.mutations.createManual);
  const createTask = useMutation(api.tasks.mutations.createTask);
  const { orgId } = useActiveOrg();

  const [title, setTitle] = useState("");
  // Type axis (MOR-707 follow-up): Event happens on the calendar with a
  // timeslot; Task is a work item with a due date. Defaults to Event unless
  // the host surface (e.g. the task board) opts into Task.
  const [kind, setKind] = useState<"event" | "task">(initialKind ?? "event");
  const [description, setDescription] = useState("");
  const [date, setDate] = useState(initialDate);
  const [allDay, setAllDay] = useState(!initialTime);
  const [startTime, setStartTime] = useState(initialTime ?? "09:00");
  const [endTime, setEndTime] = useState(
    initialEndTime ?? (initialTime ? addOneHour(initialTime) : "10:00"),
  );
  const [category, setCategory] = useState<KeyDateCategory>("meeting");
  const [selectedJobId, setSelectedJobId] = useState<Id<"jobs">>(jobId);
  // Task-only PM fields (mirror the old quick-add task form).
  const [statusId, setStatusId] = useState<string>(CREATE_NONE);
  const [priority, setPriority] = useState(0);
  const [assigneeUserId, setAssigneeUserId] = useState<string>(
    initialAssigneeUserId ?? CREATE_NONE,
  );
  const [parentTaskId, setParentTaskId] = useState<string>(
    initialParentId ?? CREATE_NONE,
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Firm statuses + members power the task-only Status / Assignee pickers;
  // job rows power the parent-task picker. Fetched once (cheap) regardless of
  // the current Type so hook order stays stable.
  const statuses = useQuery(
    api.tasks.statuses.listByOrg,
    orgId ? { orgId } : "skip",
  );
  const members = useQuery(
    api.organizations.queries.getOrganizationMembers,
    orgId ? { orgId } : "skip",
  );
  const jobRows = useQuery(api.keyDates.queries.listForJob, {
    jobId: selectedJobId,
  });

  const memberOptions = useMemo(
    () =>
      (members ?? []).map((m) => ({
        value: String(m.user._id),
        label: getDisplayName(m.user.firstName, m.user.lastName, m.user.email),
        avatar: (
          <UserAvatar
            avatarUrl={m.user.avatarUrl}
            firstName={m.user.firstName}
            lastName={m.user.lastName}
            email={m.user.email}
            className="size-4 shrink-0 text-[8px]"
            fallbackClassName="text-[8px]"
          />
        ),
      })),
    [members],
  );

  const parentOptions = useMemo(
    () =>
      (jobRows ?? [])
        .filter((e) => e.persistedId)
        .map((e) => ({ value: String(e.persistedId), label: e.title })),
    [jobRows],
  );

  // If the host page swaps the initial date (e.g. user picks a
  // different cell), reflect it in the date field.
  useEffect(() => {
    setDate(initialDate);
  }, [initialDate]);

  useEffect(() => {
    setSelectedJobId(jobId);
  }, [jobId]);

  const canSubmit = useMemo(
    () => Boolean(title.trim() && date && selectedJobId && !submitting),
    [title, date, selectedJobId, submitting],
  );

  const isEvent = kind === "event";

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      // Tasks go through `createTask` (carries the full PM field set);
      // events go through `createManual` (timing-forward, no PM stack).
      if (isEvent) {
        await createManual({
          jobId: selectedJobId,
          title: title.trim(),
          description: description.trim() || undefined,
          date,
          allDay,
          startTime: !allDay ? startTime : undefined,
          endTime: !allDay ? endTime : undefined,
          category,
          flag: "event_driven",
          kind: "event",
          source: category === "meeting" ? "meeting" : "manual",
          statusId:
            statusId !== CREATE_NONE
              ? (statusId as Id<"taskStatuses">)
              : undefined,
          priority: priority > 0 ? priority : undefined,
          assigneeUserId:
            assigneeUserId !== CREATE_NONE
              ? (assigneeUserId as Id<"users">)
              : undefined,
          parentTaskId:
            parentTaskId !== CREATE_NONE
              ? (parentTaskId as Id<"jobKeyDates">)
              : undefined,
        });
      } else {
        await createTask({
          jobId: selectedJobId,
          title: title.trim(),
          description: description.trim() || undefined,
          date,
          // Tasks don't expose the category picker (it's an event axis); store
          // a neutral category so they aren't grouped under "Meetings" etc.
          category: "custom",
          statusId:
            statusId !== CREATE_NONE
              ? (statusId as Id<"taskStatuses">)
              : undefined,
          priority: priority > 0 ? priority : undefined,
          assigneeUserId:
            assigneeUserId !== CREATE_NONE
              ? (assigneeUserId as Id<"users">)
              : undefined,
          parentTaskId:
            parentTaskId !== CREATE_NONE
              ? (parentTaskId as Id<"jobKeyDates">)
              : undefined,
        });
      }
      onClose();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Could not save — please try again.",
      );
      setSubmitting(false);
    }
  };

  return (
    <>
      <SheetHeader className="border-b p-6">
        <SheetTitle>Add new</SheetTitle>
        <SheetDescription>
          Create an event or task on this job&apos;s calendar.
        </SheetDescription>
      </SheetHeader>

      <div className="flex-1 space-y-4 overflow-y-auto p-6">
        {jobOptions && jobOptions.length > 1 && (
          <div className="space-y-1">
            <Label htmlFor="key-date-job">Job</Label>
            <Select
              value={selectedJobId}
              onValueChange={(value) => setSelectedJobId(value as Id<"jobs">)}
            >
              <SelectTrigger id="key-date-job">
                <SelectValue placeholder="Pick a job" />
              </SelectTrigger>
              <SelectContent>
                {jobOptions.map((job) => (
                  <SelectItem key={job.id} value={job.id}>
                    {job.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="space-y-1">
          <Label htmlFor="key-date-title">Title</Label>
          <Input
            id="key-date-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. First meeting of creditors"
            autoFocus
          />
        </div>

        {/* Type — Event vs Task (MOR-707 follow-up). */}
        <div className="space-y-1">
          <Label htmlFor="key-date-kind">Type</Label>
          <Select
            value={kind}
            onValueChange={(value) => setKind(value as "event" | "task")}
          >
            <SelectTrigger id="key-date-kind">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TYPE_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <Label>{isEvent ? "Date" : "Due date"}</Label>
          <DatePicker value={date} onChange={(value) => setDate(value)} />
        </div>

        <div className="space-y-1">
          <Label htmlFor="key-date-description">Description</Label>
          <Textarea
            id="key-date-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional context shown in the calendar tooltip."
            rows={2}
          />
        </div>

        {/* Timing — events only; tasks carry a due date without a timeslot. */}
        {isEvent && (
          <div className="space-y-3 rounded-md border p-3">
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="key-date-allday" className="font-medium">
                  All day
                </Label>
                <p className="text-xs text-muted-foreground">
                  Toggle off to set a specific time slot.
                </p>
              </div>
              <Switch
                id="key-date-allday"
                checked={allDay}
                onCheckedChange={setAllDay}
              />
            </div>

            {!allDay && (
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label htmlFor="key-date-start-time">Start</Label>
                  <Input
                    id="key-date-start-time"
                    type="time"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="key-date-end-time">End</Label>
                  <Input
                    id="key-date-end-time"
                    type="time"
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                  />
                </div>
              </div>
            )}

            <p className="text-xs text-muted-foreground">
              Set up a repeat from the Timing tab after saving.
            </p>
          </div>
        )}

        {/* Category — event-only (per spec). */}
        {isEvent && (
          <div className="space-y-1">
            <Label htmlFor="key-date-category">Category</Label>
            <Select
              value={category}
              onValueChange={(value) => {
                if (!isKeyDateCategory(value)) return;
                const cat = value;
                setCategory(cat);
                if (cat === "meeting") {
                  setAllDay(false);
                } else if (cat === "lodgement" || cat === "appointment") {
                  setAllDay(true);
                }
              }}
            >
              <SelectTrigger id="key-date-category">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CATEGORY_OPTIONS.map((option) => {
                  const meta = CATEGORY_META[option.value];
                  const Icon = meta.icon;
                  return (
                    <SelectItem key={option.value} value={option.value}>
                      <span className="flex items-center gap-2">
                        <Icon className={`size-3.5 ${meta.iconFgClass}`} />
                        {option.label}
                      </span>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* PM fields — status, priority, owner and an optional parent (task or
            event). Shown for both types: some events are worked like tasks. */}
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label htmlFor="key-date-status">Status</Label>
            <Select value={statusId} onValueChange={setStatusId}>
              <SelectTrigger id="key-date-status">
                <SelectValue placeholder="Default" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={CREATE_NONE}>Default</SelectItem>
                {(statuses ?? []).map((s) => (
                  <SelectItem key={String(s._id)} value={String(s._id)}>
                    <Badge color={s.color} variant="soft" size="sm">
                      {s.name}
                    </Badge>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label htmlFor="key-date-priority">Priority</Label>
            <Select
              value={String(priority)}
              onValueChange={(v) => setPriority(Number(v))}
            >
              <SelectTrigger id="key-date-priority">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TASK_PRIORITIES.map((p) => {
                  const Icon = p.icon;
                  return (
                    <SelectItem key={p.value} value={String(p.value)}>
                      <span className="flex items-center gap-2">
                        <Icon className="size-3.5" />
                        {p.label}
                      </span>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-1">
          <Label>Assignee</Label>
          <SearchableSelect
            value={assigneeUserId}
            onValueChange={setAssigneeUserId}
            options={memberOptions}
            allOptionLabel="Unassigned"
            allOptionValue={CREATE_NONE}
            placeholder="Unassigned"
            searchPlaceholder="Search members…"
            className="w-full"
          />
        </div>

        {parentOptions.length > 0 && (
          <div className="space-y-1">
            <Label>Parent</Label>
            <SearchableSelect
              value={parentTaskId}
              onValueChange={setParentTaskId}
              options={parentOptions}
              allOptionLabel="No parent"
              allOptionValue={CREATE_NONE}
              placeholder="No parent"
              searchPlaceholder="Search tasks & events…"
              className="w-full"
            />
          </div>
        )}

        {/* Subtasks are added after saving — from the item's panel, via the
            "Add subtask" button that opens a full task form. */}

        {error && (
          <p className="text-sm text-destructive" role="alert">
            {error}
          </p>
        )}
      </div>

      <SheetFooter className="flex flex-row justify-end gap-2 border-t p-6">
        <Button
          type="button"
          variant="outline"
          onClick={onClose}
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button type="button" onClick={handleSubmit} disabled={!canSubmit}>
          {isEvent ? "Save event" : "Save task"}
        </Button>
      </SheetFooter>
    </>
  );
}

// ---------------------------------------------------------------------------
// Display helpers
// ---------------------------------------------------------------------------

function formatDateLabel(iso: string) {
  try {
    return format(parseISO(iso), "EEE, d MMM yyyy");
  } catch {
    return iso;
  }
}

function flagLabel(flag: KeyDateFlag): string {
  return FLAG_OPTIONS.find((o) => o.value === flag)?.label ?? flag;
}
